<template>
  <div>
    <AlertLayout title="Reminders!" message="Kindly note that the officer will receive a notification via SMS with a link to set up their account passwords.
      <br> Kindly select the facilities, departments, and roles for the new officer. You may select two or more.
      <br> Kindly ensure the officer is placed in the correct facilities and departments with appropriate roles."
      type="warning" />
    <div v-if="error" class="mx-auto px-10 py-2">
      <AlertLayout :title="error.title" :message="error.message" type="error" />
    </div>
    <div class="mx-auto px-10 py-2 border border-t-0">
      <h2 class="text-lg font-bold mb-6">{{ officer_id ? 'Update Officer' : 'Add New Officer' }}</h2>
      <form class="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label class="block mb-1">First name <span class="text-red-500">*</span></label>
          <input type="text" v-model="formData.first_name"
            class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
            placeholder="Enter first name" required />
        </div>
        <div>
          <label class="block mb-1">Middle name</label>
          <input type="text" v-model="formData.middle_name"
            class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
            placeholder="Enter middle name" />
        </div>
        <div>
          <label class="block mb-1">Last name <span class="text-red-500">*</span></label>
          <input type="text" v-model="formData.last_name"
            class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
            placeholder="Enter last name" required />
        </div>
        <div>
          <label class="block mb-2 text-gray-700 font-medium">Gender <span class="text-red-500">*</span></label>
          <div class="relative">
            <select id="gender" v-model="formData.gender"
              class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
              required>
              <option value="" disabled class="text-gray-400">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>
        </div>
        <div>
          <label class="block mb-1">Phone number <span class="text-red-500">*</span></label>
          <input type="text" v-model="formData.phone_number"
            class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
            placeholder="Enter phone number" required />
        </div>
        <div>
          <label class="block mb-1">Email <span class="text-red-500">*</span></label>
          <input type="email" v-model="formData.email"
            class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
            placeholder="Enter email" required />
        </div>
        <div>
          <label class="block mb-2 text-gray-700 font-medium">Select contact mode <span class="text-red-500">*</span></label>
          <div class="relative">
            <select id="contact_mode" v-model="formData.contact_mode"
              class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
              required>
              <option value="" disabled class="text-gray-400">Select contact mode</option>
              <option value="both">Both</option>
              <option value="phone">Phone</option>
              <option value="email">Email</option>
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>
        </div>
        <div>
          <label class="block mb-2 text-gray-700 font-medium">Select notification type</label>
          <div class="relative">
            <select id="notification_type" v-model="formData.notification_type"
              class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none">
              <option value="" disabled class="text-gray-400">Select notification type</option>
              <option value="New Requisition">New Requisition</option>
              <option value="Start transfusion">Start transfusion</option>
              <option value="Transfusion reaction">Transfusion reaction</option>
              <option value="New Share request">New Share request</option>
              <option value="Weekly Reports">Weekly Reports</option>
              <option value="Monthly report">Monthly report</option>
              <option value="All Reports">All Reports</option>
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>
        </div>
        <div>
          <label class="block mb-2 text-gray-700 font-medium">Professional Body</label>
          <div class="relative">
            <select id="professional_body" v-model="formData.professional_body_id"
              class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
              required>
              <option value="" disabled class="text-gray-400">Select professional body</option>
              <option v-for="body in professionalBodies" :key="body.id" :value="body.id">
                {{ body.name }}
              </option>
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>
        </div>
        <div class="col-span-2">
          <label class="block mb-4 text-gray-800 font-semibold text-lg">
            Facility, Department, and Role Assignments
            <span class="text-red-500 text-base">*</span>
          </label>
          <div class="space-y-4">
            <div v-for="(userRole, index) in formData.user_roles" :key="index"
              class="group relative p-6 border-2 border-gray-200 rounded-xl bg-white hover:border-orange-200 transition-all duration-200 hover:shadow-lg">
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center space-x-2">
                  <div class="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <span class="text-orange-400 font-semibold text-sm">{{ index + 1 }}</span>
                  </div>
                  <h3 class="text-gray-700 font-medium">Assignment {{ index + 1 }}</h3>
                </div>
                <button v-if="formData.user_roles.length > 1" @click="removeUserRole(index)"
                  class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                  title="Remove Assignment">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">
                    <svg class="w-4 h-4 inline mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Facility
                  </label>
                  <div class="relative">
                    <select v-model="userRole.facility_id" @change="handleFacilityChange(index)"
                      class="w-full p-3 border-2 border-gray-200 rounded-lg bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 appearance-none cursor-pointer hover:border-gray-300"
                      required>
                      <option value="" disabled class="text-gray-400">Choose a facility...</option>
                      <option v-for="facility in facilities" :key="facility.facility_id" :value="facility.facility_id">
                        {{ facility.name }}
                      </option>
                    </select>
                    <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">
                    <svg class="w-4 h-4 inline mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Department
                  </label>
                  <div class="relative">
                    <select v-if="userRole.facility_id" v-model="userRole.facility_department_id"
                      class="w-full p-3 border-2 rounded-lg bg-white transition-all duration-200 appearance-none cursor-pointer border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 hover:border-gray-300"
                      required>
                      <option value="" disabled class="text-gray-400">Choose a department...</option>
                      <option v-for="dept in filteredDepartments(userRole.facility_id)"
                        :key="dept.facility_department_id" :value="dept.facility_department_id">
                        {{ dept.name }}
                      </option>
                    </select>
                    <select v-else v-model="userRole.facility_department_id"
                      class="w-full p-3 border-2 border-gray-100 bg-gray-50 cursor-not-allowed text-gray-400 rounded-lg appearance-none"
                      disabled>
                      <option value="" disabled class="text-gray-400">Select facility first</option>
                    </select>
                    <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg class="w-5 h-5" :class="userRole.facility_id ? 'text-gray-400' : 'text-gray-300'" fill="none"
                        stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">
                    <svg class="w-4 h-4 inline mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Role
                  </label>
                  <div class="relative">
                    <select v-model="userRole.role_id"
                      class="w-full p-3 border-2 border-gray-200 rounded-lg bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 appearance-none cursor-pointer hover:border-gray-300"
                      required>
                      <option value="" disabled class="text-gray-400">Choose a role...</option>
                      <option v-for="role in roles" :key="role.role_id" :value="role.role_id">
                        {{ role.name }}
                      </option>
                    </select>
                    <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-4 flex items-center space-x-2">
                <div class="flex space-x-1">
                  <div class="w-2 h-2 rounded-full" :class="userRole.facility_id ? 'bg-orange-400' : 'bg-gray-300'" />
                  <div class="w-2 h-2 rounded-full" :class="userRole.facility_department_id ? 'bg-orange-400' : 'bg-gray-300'" />
                  <div class="w-2 h-2 rounded-full" :class="userRole.role_id ? 'bg-orange-400' : 'bg-gray-300'" />
                </div>
                <span class="text-xs text-gray-500">{{ getCompletionText(userRole) }}</span>
              </div>
            </div>
          </div>
          <div class="mt-6 flex justify-center">
            <button @click="addUserRole"
              class="group flex items-center space-x-2 px-6 py-3 border-2 border-dashed border-orange-200 rounded-xl text-orange-400 hover:border-orange-600 hover:bg-blue-50 transition-all duration-200 font-medium">
              <svg class="w-5 h-5 group-hover:scale-110 transition-transform duration-200" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add Another Assignment</span>
            </button>
          </div>
        </div>
      </form>
      <div class="flex items-center justify-between mt-8">
        <button @click="handleBack" v-if="!officer_id"
          class="px-4 py-2 border rounded text-orange-500 border-orange-500">
          Back
        </button>
        <div class="flex items-center ml-auto">
          <button @click="resetForm" v-if="!officer_id"
            class="px-4 py-2 border rounded text-orange-500 border-orange-500">
            Cancel
          </button>
          <CustomButton v-if="!officer_id" label="Save" bg-class="bg-custom-yellow px-8 py-3 ml-3"
            @click="handleSubmitNewOfficer" />
          <CustomButton v-if="officer_id" label="Update" bg-class="bg-custom-yellow px-8 py-3 ml-3"
            @click="handleUpdateOfficer" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive } from 'vue';
import AlertLayout from "@/components/AlertLayout.vue";
import CustomButton from "@/components/CustomButton.vue";
import api from '@/services/api';
import SweetAlertService from '@/services/SweetAlertService';

export default {
  name: "NewUsersForm",
  components: {
    AlertLayout,
    CustomButton,
  },
  setup() {
    const departments = reactive([]);
    return { departments };
  },
  data() {
    return {
      user: null,
      bloodGroups: [],
      roles: [],
      facilities: [],
      professionalBodies: [],
      officer_id: '',
      userData: null,
      facility_data: null,
      facility_code: '',
      formData: {
        first_name: '',
        middle_name: '',
        last_name: '',
        gender: '',
        dob: '',
        contact_mode: '',
        email: '',
        phone_number: '',
        id_number: '',
        blood_group_id: '',
        notification_type: '',
        professional_body_id: '',
        user_roles: [{ facility_id: '', facility_department_id: '', role_id: '' }],
      },
      status: 'active',
      error: null,
    };
  },
  mounted() {
    this.initialize();
  },
  methods: {
    async initialize() {
      this.userData = this.$store.getters["auth/user"];
      if (!this.userData) {
        this.error = {
          title: 'Authentication Error',
          message: 'Please log in to access this page.',
        };
        this.$router.push({ name: 'Login' });
        return;
      }
      this.facility_data = this.userData?.user_roles[0]?.facility_department?.facility;
      this.facility_code = this.facility_data?.facility_code || '';
      console.log('Facility code:', this.facility_code);
      this.officer_id = this.$route.params.userId;
      this.fetchBloodGroups();
      this.fetchRoles();
      await this.fetchFacilities();
      await this.getDepartments();
      await this.fetchProfessionalBodies();
      if (this.officer_id) {
        await this.fetchUserData(this.officer_id);
      }
    },
    getCompletionText(userRole) {
      const completed = [userRole.facility_id, userRole.facility_department_id, userRole.role_id].filter(Boolean).length;
      return completed === 3 ? 'Complete' : `${completed}/3 selected`;
    },
    async getDepartments() {
      try {
        console.log('Fetching all departments');
        const response = await api.getRelatedDepartments();
        console.log('Raw departments response:', response);
        const newDepartments = Array.isArray(response.data) ? response.data : Array.isArray(response) ? response : [];
        console.log('Parsed departments:', newDepartments);
        this.departments.length = 0; // Clear existing departments
        newDepartments.forEach(dept => this.departments.push(dept));
        if (newDepartments.length === 0) {
          this.error = {
            title: 'No Departments Found',
            message: 'No departments available.',
          };
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
        this.error = {
          title: 'Error',
          message: error.message || 'Failed to fetch departments.',
        };
        SweetAlertService.showError('Error', 'Failed to fetch departments.');
      }
    },
    async fetchProfessionalBodies() {
      try {
        console.log('Fetching professional bodies');
        const response = await api.getProfessionalBodies();
        console.log('Raw professional bodies response:', response);
        // Handle the nested structure: { "professional_bodies": [...] }
        if (response && response.professional_bodies) {
          this.professionalBodies = response.professional_bodies;
        } else if (response && response.data && response.data.professional_bodies) {
          this.professionalBodies = response.data.professional_bodies;
        } else if (Array.isArray(response.data)) {
          this.professionalBodies = response.data;
        } else if (Array.isArray(response)) {
          this.professionalBodies = response;
        } else {
          this.professionalBodies = [];
        }
        console.log('Parsed professional bodies:', this.professionalBodies);
        if (this.professionalBodies.length === 0) {
          console.warn('No professional bodies found');
        }
      } catch (error) {
        console.error('Error fetching professional bodies:', error);
        this.error = {
          title: 'Error',
          message: error.message || 'Failed to fetch professional bodies.',
        };
        SweetAlertService.showError('Error', 'Failed to fetch professional bodies.');
      }
    },
    filteredDepartments(facilityId) {
      if (!facilityId) {
        console.log('No facility selected, returning empty departments');
        return [];
      }
      const filtered = this.departments
        .filter((dept) => {
          const hasFacility = dept.facility_departments?.some(
            (fd) => fd.facility?.facility_id === facilityId
          );
          console.log(`Filtering department ${dept.name}:`, { hasFacility, facilityId });
          return hasFacility;
        })
        .map((dept) => {
          const matchingFd = dept.facility_departments.find(
            (fd) => fd.facility?.facility_id === facilityId
          );
          return {
            name: dept.name || 'Unnamed Department',
            facility_department_id: matchingFd?.facility_department_id,
          };
        })
        .filter((dept) => dept.facility_department_id);
      console.log('Filtered departments for facility', facilityId, ':', filtered);
      return filtered;
    },
    handleFacilityChange(index) {
      const newFacilityId = this.formData.user_roles[index].facility_id;
      console.log('Facility changed for index', index, ':', newFacilityId);
      this.formData.user_roles[index].facility_department_id = '';
      this.formData.user_roles[index].role_id = '';
    },
    addUserRole() {
      this.formData.user_roles.push({ facility_id: '', facility_department_id: '', role_id: '' });
    },
    removeUserRole(index) {
      this.formData.user_roles.splice(index, 1);
    },
    populateFormData(userData) {
      this.formData.first_name = userData.first_name || '';
      this.formData.middle_name = userData.middle_name || '';
      this.formData.last_name = userData.last_name || '';
      this.formData.gender = userData.gender || '';
      this.formData.dob = userData.dob || '';
      this.formData.contact_mode = userData.contact_mode || '';
      this.formData.email = userData.email || '';
      this.formData.phone_number = userData.phone_number || '';
      this.formData.id_number = userData.id_number || '';
      this.formData.blood_group_id = userData.blood_group_id || '';
      this.formData.notification_type = userData.notification_type || '';
      this.formData.professional_body_id = userData.professional_body_id || '';
      this.formData.user_roles = userData.user_roles?.map(ur => ({
        facility_id: ur.facility_department?.facility?.facility_id || '',
        facility_department_id: ur.facility_department_id || '',
        role_id: ur.role_id || '',
      })) || [{ facility_id: '', facility_department_id: '', role_id: '' }];
    },
    validateCredentials() {
      const emptyFields = [];
      if (!this.formData.first_name) emptyFields.push('First name');
      if (!this.formData.last_name) emptyFields.push('Last name');
      if (!this.formData.gender) emptyFields.push('Gender');
      if (!this.formData.contact_mode) emptyFields.push('Contact mode');
      if (!this.formData.email && !this.formData.phone_number) {
        emptyFields.push('At least one of Email or Phone number');
      }
      this.formData.user_roles.forEach((userRole, index) => {
        if (!userRole.facility_id) emptyFields.push(`Facility for assignment ${index + 1}`);
        if (!userRole.facility_department_id) emptyFields.push(`Department for assignment ${index + 1}`);
        if (!userRole.role_id) emptyFields.push(`Role for assignment ${index + 1}`);
      });
      if (this.formData.dob) {
        const birthDate = new Date(this.formData.dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 16) {
          emptyFields.push('Age must be at least 16 years');
        }
      }
      return emptyFields;
    },
    async handleSubmitNewOfficer() {
      const errors = this.validateCredentials();
      if (errors.length > 0) {
        SweetAlertService.showError('Missing Fields', errors.join(', '));
        return;
      }
      const data = {
        first_name: this.formData.first_name,
        middle_name: this.formData.middle_name,
        last_name: this.formData.last_name,
        gender: this.formData.gender,
        dob: this.formData.dob,
        contact_mode: this.formData.contact_mode,
        email: this.formData.email,
        phone_number: this.formData.phone_number,
        id_number: this.formData.id_number,
        blood_group_id: this.formData.blood_group_id,
        notification_type: this.formData.notification_type,
        professional_body_id: this.formData.professional_body_id,
        user_roles: this.formData.user_roles.map(ur => ({
          facility_department_id: ur.facility_department_id,
          role_id: ur.role_id,
        })),
      };
      try {
        await api.createUser(data);
        await SweetAlertService.showSuccess('Success', 'Officer added successfully!');
        this.$router.push({ name: 'UserManagementPage' });
      } catch (error) {
        console.error('Error creating officer:', error);
        SweetAlertService.showError('Error', error.message || 'Failed to save officer');
      }
    },
    async handleUpdateOfficer() {
      const errors = this.validateCredentials();
      if (errors.length > 0) {
        SweetAlertService.showError('Missing Fields', errors.join(', '));
        return;
      }
      const data = {
        first_name: this.formData.first_name,
        middle_name: this.formData.middle_name,
        last_name: this.formData.last_name,
        gender: this.formData.gender,
        dob: this.formData.dob,
        contact_mode: this.formData.contact_mode,
        email: this.formData.email,
        phone_number: this.formData.phone_number,
        id_number: this.formData.id_number,
        blood_group_id: this.formData.blood_group_id,
        notification_type: this.formData.notification_type,
        professional_body_id: this.formData.professional_body_id,
        user_roles: this.formData.user_roles.map(ur => ({
          facility_department_id: ur.facility_department_id,
          role_id: ur.role_id,
        })),
      };
      try {
        await api.updateUser(this.officer_id, data);
        await SweetAlertService.showSuccess('Success', 'Officer updated successfully!');
        this.$router.push({ name: 'UserManagementPage' });
      } catch (error) {
        console.error('Error updating officer:', error);
        SweetAlertService.showError('Error', error.message || 'Failed to update officer');
      }
    },
    async fetchBloodGroups() {
      try {
        const response = await api.getBloodGroups();
        this.bloodGroups = response.data || response;
      } catch (error) {
        console.error('Error fetching blood groups:', error);
      }
    },
    async fetchFacilities() {
      if (!this.userData) {
        this.error = {
          title: 'Authentication Error',
          message: 'Please log in to access facilities.',
        };
        return;
      }
      if (!this.facility_code) {
        try {
          const response = await api.getAllFacilities();
          this.facilities = response.data || response || [];
          console.log('Fetched all facilities:', this.facilities);
        } catch (error) {
          console.error('Error fetching all facilities:', error);
          this.error = {
            title: 'Error',
            message: 'Failed to load facilities. Please try again.',
          };
        }
        return;
      }
      try {
        console.log('Fetching facilities for facility code:', this.facility_code);
        const response = await api.getRelatedFacilities(this.facility_code);
        this.facilities = response.data || response || [];
        console.log('Fetched facilities:', this.facilities);
        if (this.facilities.length === 0) {
          this.error = {
            title: 'No Facilities Found',
            message: 'No facilities available for the current user. Contact support.',
          };
        }
      } catch (error) {
        console.error('Error fetching facilities:', error);
        this.error = {
          title: 'Error',
          message: error.message || 'Failed to fetch facilities.',
        };
      }
    },
    async fetchRoles() {
      try {
        const response = await api.getRoles();
        this.roles = response.data || response;
        console.log('Fetched roles:', this.roles);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    },
    async fetchUserData(userId) {
      try {
        const response = await api.getUser(userId);
        this.populateFormData(response.data || response);
      } catch (error) {
        console.error('Error fetching user data:', error);
        this.error = {
          title: 'Error',
          message: 'Failed to fetch user data.',
        };
      }
    },
    resetForm() {
      this.formData = {
        first_name: '',
        middle_name: '',
        last_name: '',
        gender: '',
        dob: '',
        contact_mode: '',
        email: '',
        phone_number: '',
        id_number: '',
        blood_group_id: '',
        notification_type: '',
        professional_body_id: '',
        user_roles: [{ facility_id: '', facility_department_id: '', role_id: '' }],
      };
      this.departments.length = 0;
      this.error = null;
    },
    handleBack() {
      this.$router.push({ name: 'UserManagementPage' });
    },
  },
};
</script>

<style lang="css" scoped>
/* Your existing styles */
</style>